import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Popconfirm } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import CapMark from 'components/Global/ChatBubble/CapMark';
import CapWordmark from 'components/Global/ChatBubble/CapWordmark';
import SkillsManager from 'components/Global/ChatBubble/SkillsManager';
import { deleteApi, getApi, putApi } from 'redux/apis';
import { bumpConversations, clearConversationUnread, clearPendingNewChat, openConversation, startNewChat } from 'redux/slice/chatBubbleSlice';
import { AppDispatch } from 'redux/store';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';

interface ChatNavProps {
	collapsed?: boolean;
}

interface ConversationSummary {
	id: string;
	title: string | null;
	lastMessageAt: string;
}

const PlusIcon = () => (
	<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
		<path
			d="M12 5v14M5 12h14"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	</svg>
);

const SkillsIcon = () => (
	<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
		<path
			d="M12 3l2.1 4.6L19 8.3l-3.5 3.3.9 4.9L12 14.9 7.6 16.5l.9-4.9L5 8.3l4.9-.7L12 3z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinejoin="round"
		/>
	</svg>
);

// The assistant's own section of the global sidebar: the CAP mark, a way to
// start a fresh chat, and the saved threads behind it. Opening, renaming and
// deleting a thread all act on the one ChatBubble mounted on the /chat page
// rather than holding any chat state here.
const ChatNav = ({ collapsed = false }: ChatNavProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	// "New chat" used to carry a filled background at all times, which read as
	// a selected nav item — so the assistant looked active while you were on
	// the dashboard. It is filled only when /chat is actually the open page.
	const onChatPage = useLocation().pathname === '/chat';

	// The saved threads shown under "New chat". Refetched whenever a turn is
	// persisted (conversationsVersion, bumped by ChatBubble) so a new or renamed
	// thread appears without a reload.
	const conversationsVersion = useSelector(
		(state: any) => state.chatBubble?.conversationsVersion ?? 0
	);
	// The thread currently open on /chat, highlighted as selected in the list.
	const activeConversationId = useSelector(
		(state: any) => state.chatBubble?.activeConversationId ?? null
	);
	// The organization currently selected in the header switcher. Chat history is
	// scoped per user AND per company, so the list is filtered to this company and
	// refetched whenever it changes.
	const activeCompanyId = useSelector(
		(state: any) => state.companies?.selectedCompanyDetails?.company?.id ?? null
	);
	// Threads whose reply arrived while the user was off /chat — shown with a dot.
	const unreadConversationIds = useSelector(
		(state: any) => state.chatBubble?.unreadConversationIds ?? {}
	);
	// The chat currently generating a reply — shown as "working" (a spinner on its
	// row, or a temporary row for a brand-new chat that has no saved id yet).
	const runningConversationIds = useSelector(
		(state: any) => state.chatBubble?.runningConversationIds ?? {}
	);
	const pendingNewChat = useSelector(
		(state: any) => state.chatBubble?.pendingNewChat ?? null
	);
	const [conversations, setConversations] = useState<ConversationSummary[]>([]);
	// The per-company skills manager modal, opened from the button under "New chat".
	const [skillsOpen, setSkillsOpen] = useState(false);
	// The thread being renamed inline, and its working title text.
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingTitle, setEditingTitle] = useState('');
	// Shows skeleton rows while the new company's threads load — on first load and
	// on an org switch, but NOT on a silent refetch after sending a message.
	const [loadingThreads, setLoadingThreads] = useState(false);
	const prevCompanyRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		// Until a company is resolved, don't show another company's threads.
		if (!activeCompanyId) {
			setConversations([]);
			prevCompanyRef.current = null;
			return;
		}
		const isSwitchOrFirstLoad = prevCompanyRef.current !== activeCompanyId;
		prevCompanyRef.current = activeCompanyId;
		if (isSwitchOrFirstLoad) {
			setLoadingThreads(true);
			setConversations([]);
		}
		getApi('/ai/conversations', { limit: 30, companyId: activeCompanyId })
			.then((res) => {
				if (cancelled) return;
				const list = res?.data?.data?.conversations;
				if (Array.isArray(list)) {
					setConversations(list);
				}
			})
			.catch(() => {
				// A failed history fetch just leaves the list empty — never blocks
				// the sidebar or "New chat".
			})
			.finally(() => {
				if (!cancelled) setLoadingThreads(false);
			});
		return () => {
			cancelled = true;
		};
	}, [conversationsVersion, activeCompanyId]);

	const openNewChat = () => {
		dispatch(startNewChat());
		navigate('/chat');
	};

	const openThread = (id: string) => {
		dispatch(clearConversationUnread(id));
		dispatch(openConversation(id));
		navigate('/chat');
	};

	const startRename = (conversation: ConversationSummary) => {
		setEditingId(conversation.id);
		setEditingTitle(conversation.title || '');
	};

	const submitRename = async (id: string) => {
		const title = editingTitle.trim();
		setEditingId(null);
		const current = conversations.find((c) => c.id === id);
		if (!title || title === (current?.title || '')) {
			return;
		}
		// Optimistic: show the new name immediately, revert via a refetch on failure.
		setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
		try {
			await putApi(`/ai/conversations/${id}`, { title });
		} catch {
			toastText('Could not rename the chat.', 'error');
			dispatch(bumpConversations());
		}
	};

	const handleDelete = async (id: string) => {
		const wasActive = id === activeConversationId;
		// Optimistic removal.
		setConversations((prev) => prev.filter((c) => c.id !== id));
		try {
			await deleteApi(`/ai/conversations/${id}`);
			// Deleting the open thread clears the /chat panel to a fresh chat.
			if (wasActive) {
				dispatch(startNewChat());
				navigate('/chat');
			}
		} catch {
			toastText('Could not delete the chat.', 'error');
			dispatch(bumpConversations());
		}
	};

	// Kept in the server's most-recent-first order (by lastMessageAt). A chat
	// rises to the top only when it gets a new message — not merely when it's
	// clicked — so opening an older thread highlights it in place instead of
	// yanking it to the top.
	const orderedConversations = conversations;

	// A brand-new chat shows a temporary "working" row until its real saved row
	// lands in the list (after the post-save refetch). Once that real row is
	// present it carries the spinner itself, so the temporary row is dropped in
	// the SAME render that reveals the real one — avoiding both the gap where the
	// loader blinks off (adopt cleared the temp row before the refetch returned)
	// and a flash of two rows.
	const realRunningRowPresent = orderedConversations.some((c) => runningConversationIds[c.id]);
	useEffect(() => {
		if (pendingNewChat && realRunningRowPresent) {
			dispatch(clearPendingNewChat());
		}
	}, [pendingNewChat, realRunningRowPresent, dispatch]);

	// Collapsed, the rail is 80px of icon column — a thread title can't survive
	// that, so only the way in does.
	if (collapsed) {
		return (
			<div className={styles['chat-nav--collapsed']}>
				<button
					type="button"
					className={`${styles['chat-nav__icon-btn']} ${onChatPage ? styles['chat-nav__icon-btn--on'] : ''}`}
					aria-current={onChatPage ? 'page' : undefined}
					title="New chat"
					aria-label="New chat"
					onClick={openNewChat}
				>
					<CapMark size={20} tone="light" />
				</button>
			</div>
		);
	}

	return (
		<div className={styles['chat-nav']}>
			<div className={styles['chat-nav__lockup']}>
				<CapWordmark height={18} />
				<CapMark size={13} tone="light" className={styles['chat-nav__spark']} />
				<button
					type="button"
					className={styles['chat-nav__skills']}
					onClick={() => setSkillsOpen(true)}
					title="Manage the assistant's skills"
				>
					<SkillsIcon />
					<span>Skills</span>
				</button>
			</div>

			<button
				type="button"
				// Filled only for a fresh, empty chat — i.e. on /chat with no thread
				// selected. Once a saved thread is open, that thread carries the
				// "you are here" highlight, so filling New chat too would mark two
				// rows as selected at once.
				className={`${styles['chat-nav__new']} ${onChatPage && !activeConversationId ? styles['chat-nav__new--on'] : ''}`}
				aria-current={onChatPage && !activeConversationId ? 'page' : undefined}
				onClick={openNewChat}
			>
				<span className={styles['chat-nav__new-glyph']}>
					<PlusIcon />
				</span>
				New chat
			</button>

			<SkillsManager open={skillsOpen} onClose={() => setSkillsOpen(false)} companyId={activeCompanyId} />

			<div className={styles['chat-nav__list']}>
				{loadingThreads &&
					Array.from({ length: 6 }).map((_, i) => (
						<div key={`sk-${i}`} className={styles['chat-nav__skeleton']} aria-hidden="true" />
					))}
				{!loadingThreads && pendingNewChat && !realRunningRowPresent && (
					<div className={`${styles['chat-nav__row']} ${styles['chat-nav__row--running']}`}>
						<span className={styles['chat-nav__spinner']} aria-hidden="true" />
						<span className={styles['chat-nav__running-label']}>{pendingNewChat.title || 'New chat'}</span>
					</div>
				)}
				{!loadingThreads &&
					orderedConversations.map((conversation) => {
					const isActive = conversation.id === activeConversationId && onChatPage;
					const isEditing = conversation.id === editingId;
					const isUnread = !!unreadConversationIds[conversation.id];
					const isRunning = !!runningConversationIds[conversation.id];
					const label = conversation.title || 'Untitled chat';
					return (
						<div
							key={conversation.id}
							className={`${styles['chat-nav__row']} ${isActive ? styles['chat-nav__row--active'] : ''} ${isUnread ? styles['chat-nav__row--unread'] : ''} ${isRunning ? styles['chat-nav__row--running'] : ''}`}
						>
							{isRunning && !isEditing && (
								<span className={styles['chat-nav__spinner']} aria-hidden="true" />
							)}
							{isUnread && !isRunning && !isEditing && (
								<span className={styles['chat-nav__unread-dot']} title="New reply" aria-label="New reply" />
							)}
							{isEditing ? (
								<input
									className={styles['chat-nav__edit']}
									value={editingTitle}
									autoFocus
									maxLength={120}
									onChange={(e) => setEditingTitle(e.target.value)}
									onBlur={() => submitRename(conversation.id)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											submitRename(conversation.id);
										} else if (e.key === 'Escape') {
											setEditingId(null);
										}
									}}
								/>
							) : (
								<>
									<button
										type="button"
										className={styles['chat-nav__item']}
										title={label}
										onClick={() => openThread(conversation.id)}
									>
										{label}
									</button>
									{!isRunning && (
						<div className={styles['chat-nav__row-actions']}>
										<button
											type="button"
											className={styles['chat-nav__action']}
											title="Rename"
											aria-label="Rename chat"
											onClick={(e) => {
												e.stopPropagation();
												startRename(conversation);
											}}
										>
											<EditOutlined />
										</button>
										<Popconfirm
											rootClassName="cap-chat-confirm"
											icon={null}
											title="Delete this chat?"
											description="This conversation will be permanently removed."
											okText="Delete"
											cancelText="Cancel"
											okButtonProps={{ danger: true }}
											placement="topRight"
											onConfirm={() => handleDelete(conversation.id)}
										>
											<button
												type="button"
												className={styles['chat-nav__action']}
												title="Delete"
												aria-label="Delete chat"
												onClick={(e) => e.stopPropagation()}
											>
												<DeleteOutlined />
											</button>
										</Popconfirm>
									</div>
								)}
								</>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default ChatNav;
